import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { Link } from "react-router-dom";

export interface ArticleBodyProps {
  markdown: string;
  className?: string;
}

const schema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), "h2", "h3", "h4", "blockquote", "hr"],
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a ?? []), "href", "target", "rel"],
    h2: [...(defaultSchema.attributes?.h2 ?? []), "id"],
    h3: [...(defaultSchema.attributes?.h3 ?? []), "id"],
    h4: [...(defaultSchema.attributes?.h4 ?? []), "id"],
  },
};

export function ArticleBody({ markdown, className }: ArticleBodyProps) {
  return (
    <article
      className={
        "prose prose-neutral max-w-none " +
        "prose-headings:text-display prose-headings:tracking-tight prose-headings:font-medium " +
        "prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 " +
        "prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 " +
        "prose-p:text-base prose-p:leading-relaxed prose-p:text-foreground " +
        "prose-a:text-accent-press prose-a:underline-offset-4 hover:prose-a:opacity-80 " +
        "prose-strong:text-foreground " +
        "prose-blockquote:border-l-accent-press prose-blockquote:border-l-4 prose-blockquote:pl-5 prose-blockquote:italic prose-blockquote:text-muted-foreground " +
        "prose-ul:my-4 prose-li:my-1 " +
        "first-of-type:[&>p:first-of-type]:first-letter:text-display first-of-type:[&>p:first-of-type]:first-letter:float-left first-of-type:[&>p:first-of-type]:first-letter:mr-2 first-of-type:[&>p:first-of-type]:first-letter:text-[3.5em] first-of-type:[&>p:first-of-type]:first-letter:leading-[0.9] first-of-type:[&>p:first-of-type]:first-letter:font-medium " +
        (className ?? "")
      }
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug, [rehypeAutolinkHeadings, { behavior: "wrap" }], [rehypeSanitize, schema]]}
        components={{
          a: ({ href, children, ...rest }) => {
            const url = href ?? "";
            if (url.startsWith("/")) {
              return (
                <Link to={url} {...rest}>
                  {children}
                </Link>
              );
            }
            return (
              <a href={url} target="_blank" rel="noopener noreferrer" {...rest}>
                {children}
              </a>
            );
          },
        }}
      >
        {markdown}
      </ReactMarkdown>
    </article>
  );
}
